import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import PartnerCard from "@/components/landing/PartnerCard";
import { REGIONAL_COVERAGE } from "@/data/regionalCoverage";

/**
 * /regional-coverage — Regional Coverage profiles page.
 *
 * Mirrors /leadership: profiles render through the shared PartnerCard so
 * the layout is identical across partnership, leadership and regional
 * coverage tiers.
 */
const RegionalCoveragePage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid="kr-regional-coverage-page"
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Regional Coverage"
          label="Regional Coverage · On-the-Ground Representation · By Introduction"
          title={
            <>
              The partnership&apos;s{" "}
              <span className="italic text-[var(--kr-gold)]">
                on-the-ground
              </span>{" "}
              presence in its coverage markets.
            </>
          }
          description="Regional Coverage comprises regionally-based representatives who are the partnership's contact point in a market — leading investor dialogue and facilitating introductions that lead back to the partnership's Capital Formation function. Regional leads engage on documented, non-executive terms, do not hold partner economics and do not act as placement agents or authorised representatives."
          breadcrumb="Regional Coverage"
        />

        <section
          data-testid="kr-regional-coverage-profiles"
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="block w-[8px] h-[8px]"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Regional Coverage
              </span>
              <span className="flex-1 h-px bg-[var(--kr-line)]" />
            </div>

            <div className="space-y-0 border-t border-[var(--kr-line-soft)]">
              {REGIONAL_COVERAGE.map((m, i) => (
                <PartnerCard key={m.id} partner={m} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing note */}
        <section
          className="py-20 lg:py-24 border-t border-[var(--kr-line-soft)]"
          style={{ background: "var(--kr-bg-2)" }}
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Basis of Engagement
                  </span>
                  <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.2] max-w-[34ch]">
                  Regional coverage is{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    introductory
                  </span>{" "}
                  — engagement is concluded by the partnership.
                </h2>
                <p className="mt-6 text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[64ch]">
                  Regional coverage does not constitute a regulated office,
                  branch or licensed activity in any coverage market. Regional
                  leads conduct dialogue on an introductory basis under the
                  partnership&apos;s Selective Capital Pathways framework;
                  investment discussions, diligence and documentation are
                  conducted by the partnership directly.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <Link
                  to="/gulf-investors"
                  data-testid="kr-regional-coverage-gulf-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  For Gulf Investors →
                </Link>
                <Link
                  to="/team"
                  data-testid="kr-regional-coverage-team-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Return to Team →
                </Link>
                <Link
                  to="/capital-pathways"
                  data-testid="kr-regional-coverage-pathways-link"
                  className="kr-link font-mono-label text-[11px]"
                >
                  Selective Capital Pathways →
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default RegionalCoveragePage;
